// Cascade-testing candidates (pure). See plan §5.4 + PLAN-cascade-priors-
// inheritance-aware.md. From each "index" for a disease (affected, or a
// tested/obligate carrier) we walk out to untested, unaffected relatives and
// assign a carrier/at-risk prior. The prior is now inheritance-mode-aware
// (cascadePriors): dominant families use the 0.5/0.25/0.125 degree ladder;
// autosomal-recessive families use carrier math (obligate parents/children,
// 2/3 sibs). XLR/MT/YL currently fall back to the dominant ladder (deferred).
// Probabilities are also conditioned on tested intermediates: a relative reached
// only through a confirmed non-carrier is pruned. Minors are deferred.

import { RECOMMENDED_ACTION, isCarrierMark } from '../constants';
import { isAffectedBy } from './inheritance.helpers';
import {
  resolveCascadeMode,
  cascadePrior,
  CASCADE_DEPTH_CAP
} from '../cascadePriors';
import { memberHasGenotypeResult } from '../cascadeScreening';

const MINOR_AGE = 18;

// Confirmed-negative genotype values — used to prune transmission through a
// relative who has tested negative for the gene.
const NEGATIVE_STATUSES = new Set(['non-carrier', 'homozygous-normal']);

const carrierState = (ind, diseaseId) =>
  ind.carrierByDisease ? ind.carrierByDisease[diseaseId] : undefined;

const isKnownCarrier = (ind, diseaseId) =>
  isCarrierMark(carrierState(ind, diseaseId));

const isIndex = (ind, diseaseId) =>
  isAffectedBy(ind, diseaseId) || isKnownCarrier(ind, diseaseId);

const get = (ped, id) => (id ? ped.byPid.get(id) : null);

function eligible(ind, diseaseId) {
  return (
    ind &&
    !ind.isDummy &&
    !ind.deceased &&
    !isAffectedBy(ind, diseaseId) &&
    !isKnownCarrier(ind, diseaseId)
  );
}

// A confirmed non-carrier for the (optional) gene — and not itself an index.
function isTestedNegativeForGene(ind, gene) {
  if (!ind) return false;
  return (ind.genotypes || []).some((g) => {
    const status = String((g && g.carrierStatus) || '').trim();
    if (!NEGATIVE_STATUSES.has(status)) return false;
    if (!gene) return true;
    return (
      String((g && g.gene) || '').toUpperCase() === String(gene).toUpperCase()
    );
  });
}

// The family's implicated gene(s) for a disease, from index genotypes carrying
// an asserted status. Exactly one → targeted test; several/none → panel.
function familyGenes(indexes) {
  const genes = new Set();
  for (const ind of indexes)
    for (const g of ind.genotypes)
      if (g.gene && g.carrierStatus) genes.add(g.gene);
  return [...genes];
}

// Relatives of `index` to 3rd degree, each tagged with its kind, degree, and the
// FIRST-DEGREE intermediate on the path (`via`) used for transmission
// conditioning. `via` is null for first-degree relatives.
function enumerateRelatives(ped, index) {
  const out = [];
  const seen = new Set([index.pid]);
  const add = (pid, kind, degree, via) => {
    if (pid == null || seen.has(pid)) return null;
    const ind = ped.byPid.get(pid);
    if (!ind) return null;
    seen.add(pid);
    out.push({ pid, kind, degree, via });
    return ind;
  };

  // Degree 1
  const parents = [];
  for (const pid of [index.fatherId, index.motherId]) {
    const p = add(pid, 'parent', 1, null);
    if (p) parents.push(p);
  }
  const children = [];
  for (const cid of index.childrenIds) {
    const c = add(cid, 'child', 1, null);
    if (c) children.push(c);
  }
  const sibs = [];
  for (const sid of index.siblingIds) {
    const s = add(sid, 'fullSib', 1, null);
    if (s) sibs.push(s);
  }

  // Degree 2 (via a first-degree relative)
  const grandparents = [];
  for (const p of parents)
    for (const gpid of [p.fatherId, p.motherId]) {
      const gp = add(gpid, 'grandparent', 2, p.pid);
      if (gp) grandparents.push({ ind: gp, via: p.pid });
    }
  for (const p of parents)
    for (const aid of p.siblingIds) add(aid, 'auntUncle', 2, p.pid);
  const grandchildren = [];
  for (const c of children)
    for (const gcid of c.childrenIds) {
      const gc = add(gcid, 'grandchild', 2, c.pid);
      if (gc) grandchildren.push({ ind: gc, via: c.pid });
    }
  const niblings = [];
  for (const s of sibs)
    for (const nid of s.childrenIds) {
      const nn = add(nid, 'nieceNephew', 2, s.pid);
      if (nn) niblings.push({ ind: nn, via: s.pid });
    }

  // Degree 3 (carry the first-degree intermediate forward as the conditioning gate)
  for (const { ind: gp, via } of grandparents) {
    for (const ggid of [gp.fatherId, gp.motherId])
      add(ggid, 'greatGrandparent', 3, via);
    for (const gaid of gp.siblingIds) add(gaid, 'greatAuntUncle', 3, via);
  }
  for (const p of parents)
    for (const aid of p.siblingIds) {
      const a = ped.byPid.get(aid);
      if (a) for (const cid of a.childrenIds) add(cid, 'firstCousin', 3, p.pid);
    }
  for (const { ind: gc, via } of grandchildren)
    for (const ggcid of gc.childrenIds) add(ggcid, 'greatGrandchild', 3, via);
  for (const { ind: nn, via } of niblings)
    for (const gnid of nn.childrenIds) add(gnid, 'grandNieceNephew', 3, via);

  return out;
}

export function detectCascadeCandidates(
  ped,
  diseaseId,
  inheritancePattern = null
) {
  const indexes = ped.individuals.filter((i) => isIndex(i, diseaseId));
  if (indexes.length === 0) return [];

  const mode = resolveCascadeMode(inheritancePattern);
  const genes = familyGenes(indexes);
  const targetGene = genes.length === 1 ? genes[0] : null;

  // pid → best candidate (highest prior wins on dedupe across indexes/paths).
  const byPid = new Map();
  const consider = (ind, kind, degree, via, indexAffected) => {
    if (!eligible(ind, diseaseId)) return;
    // Already tested for the implicated gene → not a screening candidate: a
    // positive result makes them an index, a negative one resolves them. Keyed
    // off the raw genotype record (not the projected per-disease carrier
    // status) so it holds even when the gene→disease terminology link is
    // incomplete — which is exactly when a positive would otherwise slip the
    // isKnownCarrier check above and surface a spurious badge.
    if (memberHasGenotypeResult(ind, targetGene)) return;
    // Conditioning: prune if the connecting first-degree intermediate is a
    // confirmed non-carrier (the allele didn't pass that way). A positive
    // intermediate is itself an index, so its descendants are already covered.
    if (via != null && isTestedNegativeForGene(get(ped, via), targetGene))
      return;

    const { prior, band } = cascadePrior(mode, { kind, degree, indexAffected });
    if (!(prior > 0)) return; // mode says this relative isn't at risk

    const existing = byPid.get(ind.pid);
    if (existing && existing.priorProbability >= prior) return;

    const minor =
      ind.ageAtAssessment != null && ind.ageAtAssessment < MINOR_AGE;
    byPid.set(ind.pid, {
      pid: ind.pid,
      token: ind.token,
      degree,
      priorProbability: prior,
      band,
      obligate: band === 'obligate',
      isConditional: degree > 1,
      conditionalOn: via != null ? via : null,
      variant: { gene: targetGene, targetVariant: null },
      recommendedAction: minor
        ? RECOMMENDED_ACTION.defer
        : targetGene
        ? RECOMMENDED_ACTION.targetedTest
        : RECOMMENDED_ACTION.panelTest,
      deferReason: minor ? 'minor' : null
    });
  };

  for (const index of indexes) {
    const indexAffected = isAffectedBy(index, diseaseId);
    for (const rel of enumerateRelatives(ped, index)) {
      if (rel.degree > CASCADE_DEPTH_CAP) continue;
      consider(get(ped, rel.pid), rel.kind, rel.degree, rel.via, indexAffected);
    }
  }

  // Order: lower degree first, then descending prior, then youngest age.
  return [...byPid.values()].sort((a, b) => {
    if (a.degree !== b.degree) return a.degree - b.degree;
    if (a.priorProbability !== b.priorProbability)
      return b.priorProbability - a.priorProbability;
    const ageA = get(ped, a.pid).ageAtAssessment ?? Infinity;
    const ageB = get(ped, b.pid).ageAtAssessment ?? Infinity;
    return ageA - ageB;
  });
}
